import type { ChartOptions } from 'chart.js';

/**
 * Scale factor for Chart.js fonts when rendering to a 300 DPI canvas.
 * Chart.js defaults to 12px which looks correct at ~96 DPI screen resolution.
 * At 300 DPI: 12px * 3.5 = 42px = ~10pt (readable print size).
 */
const PRINT_FONT_SCALE = 3.5;

/** Extract font size from a Chart.js font option, defaulting to 12 */
function getFontSize(fontOrObj: unknown): number {
  if (typeof fontOrObj === 'object' && fontOrObj !== null && 'size' in fontOrObj) {
    return (fontOrObj as { size?: number }).size ?? 12;
  }
  return 12;
}

/**
 * Capture a Chart.js chart at high resolution for PDF export.
 *
 * Creates an off-screen Chart.js instance with print-friendly colors (dark text
 * on white), the specified dimensions, and font sizes scaled for 300 DPI.
 *
 * IMPORTANT: Uses sourceChart.config.options (the original user-provided options,
 * plain objects) rather than sourceChart.options (Chart.js resolved options wrapped
 * in Proxies). Chart.js 4 Proxies have a broken getOwnPropertyDescriptor trap that
 * crashes when Chart.js's internal merge/clone tries to deep-copy them.
 */
export async function captureChartHighRes(
  canvas: HTMLCanvasElement | null | undefined,
  targetWidth: number,
  targetHeight: number
): Promise<HTMLCanvasElement | null> {
  if (!canvas || canvas.width === 0) return null;

  const { Chart } = await import('chart.js');
  const sourceChart = Chart.getChart(canvas);
  if (!sourceChart) {
    const copy = document.createElement('canvas');
    copy.width = canvas.width;
    copy.height = canvas.height;
    copy.getContext('2d')?.drawImage(canvas, 0, 0);
    return copy;
  }

  // Skip charts with no data - Chart.js collapses axes/structure without data points
  const hasData = sourceChart.data.datasets?.some(ds => (ds.data?.length ?? 0) > 0) ?? false;
  if (!hasData) return null;

  // Create off-screen canvas for a temporary chart (never mutate the live chart)
  const offCanvas = document.createElement('canvas');
  offCanvas.width = targetWidth;
  offCanvas.height = targetHeight;
  offCanvas.style.position = 'fixed';
  offCanvas.style.left = '-9999px';
  offCanvas.style.visibility = 'hidden';
  document.body.appendChild(offCanvas);

  try {
    const printColor = '#333333';
    const printGridColor = 'rgba(0, 0, 0, 0.1)';

    // Use original config options (plain objects), NOT resolved options (Proxy-wrapped).
    // chart.config.options contains the user-provided options before Chart.js wraps
    // them in Proxies. This avoids the getOwnPropertyDescriptor Proxy trap violations
    // that occur when Chart.js internally merges/clones Proxy-wrapped objects.
    const configOptions = (sourceChart.config.options ?? {}) as Record<string, unknown>;

    // Clone scale options, apply print colors, and scale fonts for 300 DPI
    const originalScales = (configOptions.scales ?? {}) as Record<string, Record<string, unknown>>;
    const printScales: Record<string, unknown> = {};
    for (const [key, scale] of Object.entries(originalScales)) {
      if (scale && typeof scale === 'object') {
        const ticksObj = (scale.ticks ?? {}) as Record<string, unknown>;
        const titleObj = (scale.title ?? {}) as Record<string, unknown>;
        printScales[key] = {
          ...scale,
          ticks: {
            ...ticksObj,
            color: printColor,
            font: {
              ...((ticksObj.font as object) ?? {}),
              size: Math.round(getFontSize(ticksObj.font) * PRINT_FONT_SCALE),
            },
          },
          title: {
            ...titleObj,
            color: printColor,
            font: {
              ...((titleObj.font as object) ?? {}),
              size: Math.round(getFontSize(titleObj.font) * PRINT_FONT_SCALE),
            },
          },
          grid: { ...((scale.grid as object) ?? {}), color: printGridColor },
        };
      }
    }

    // Clone plugin options, apply print colors, and scale fonts
    const originalPlugins = (configOptions.plugins ?? {}) as Record<string, unknown>;
    const originalLegend = (originalPlugins.legend ?? {}) as Record<string, unknown>;
    const originalLegendLabels = (originalLegend.labels ?? {}) as Record<string, unknown>;
    const originalTitle = (originalPlugins.title ?? {}) as Record<string, unknown>;
    const printPlugins = {
      ...originalPlugins,
      legend: {
        ...originalLegend,
        labels: {
          ...originalLegendLabels,
          color: printColor,
          font: {
            ...((originalLegendLabels.font as object) ?? {}),
            size: Math.round(getFontSize(originalLegendLabels.font) * PRINT_FONT_SCALE),
          },
        },
      },
      title: {
        ...originalTitle,
        color: printColor,
        font: {
          ...((originalTitle.font as object) ?? {}),
          size: Math.round(getFontSize(originalTitle.font) * PRINT_FONT_SCALE),
        },
      },
    };

    const chartType = 'type' in sourceChart.config ? sourceChart.config.type : 'line';
    const tempChart = new Chart(offCanvas, {
      type: chartType,
      data: sourceChart.data,
      options: {
        ...configOptions,
        responsive: false,
        maintainAspectRatio: false,
        // DPR=1: canvas is already at target 300 DPI dimensions, no need to double
        devicePixelRatio: 1,
        animation: false,
        // Default font size scaled for 300 DPI (cascades to all unset font sizes)
        font: {
          ...((configOptions.font as object) ?? {}),
          size: Math.round(12 * PRINT_FONT_SCALE),
        },
        scales: printScales as ChartOptions['scales'],
        plugins: printPlugins as ChartOptions['plugins'],
      },
    });

    // Copy the rendered canvas (dimensions match target since DPR=1)
    const copy = document.createElement('canvas');
    copy.width = offCanvas.width;
    copy.height = offCanvas.height;
    const copyCtx = copy.getContext('2d');
    if (copyCtx) {
      copyCtx.drawImage(offCanvas, 0, 0);
    }

    tempChart.destroy();
    return copy;
  } finally {
    document.body.removeChild(offCanvas);
  }
}
